import { watch } from 'vue'
import { registerSW } from 'virtual:pwa-register'
import { AppUpdateManager } from './app-update'
import { flushReloadSaves, reloadSafety } from './reload-safety'

export const UPDATE_CHECK_INTERVAL_MS = 5 * 60_000
export const UPDATE_CHECK_THROTTLE_MS = 60_000

interface Runtime {
  active(): boolean
  blocked(): boolean
  finishRecord(): Promise<void>
  refreshControls(): void
}
let runtime: Runtime | undefined
let registration: ServiceWorkerRegistration | undefined
let activateSW: () => Promise<void> = async () => { throw new Error('Update not registered') }

export const appUpdates = new AppUpdateManager({
  now: () => Date.now(),
  navigationStartedAt: () => performance.timeOrigin,
  visible: () => document.visibilityState === 'visible',
  active: () => runtime?.active() ?? false,
  blocked: () => !runtime || runtime.blocked() || reloadSafety.blockers > 0,
  lock: (value) => {
    reloadSafety.preparing = value
    runtime?.refreshControls()
  },
  prepare: async (manual) => {
    if (manual) await runtime?.finishRecord()
    await flushReloadSaves()
  },
  activate: async () => {
    if (!registration?.waiting) throw new Error('No waiting update')
    await activateSW()
  },
  reload: () => window.location.reload(),
  storage: () => window.sessionStorage,
})

export function initializeAppUpdates(dependencies: Runtime): () => void {
  runtime = dependencies
  let lastCheck = -Infinity
  let checking: Promise<void> | undefined
  let interval: ReturnType<typeof setInterval> | undefined
  let disposed = false
  const serviceWorkers = navigator.serviceWorker
  let controller = serviceWorkers?.controller

  const check = () => {
    if (!registration || checking || navigator.onLine === false || Date.now() - lastCheck < UPDATE_CHECK_THROTTLE_MS) return
    lastCheck = Date.now()
    const target = registration
    checking = Promise.resolve().then(() => target.update()).then(() => undefined).catch(() => undefined).finally(() => { checking = undefined })
  }
  const visibility = () => {
    if (document.visibilityState === 'visible') check()
    appUpdates.reconsider()
  }
  const online = () => {
    check()
    appUpdates.reconsider()
  }
  const changed = () => {
    const next = serviceWorkers?.controller
    if (!next || next === controller) return
    const wasControlled = Boolean(controller)
    controller = next
    // Claiming an uncontrolled page for the first installation is not an update.
    if (wasControlled || appUpdates.state.pending) appUpdates.controllerChanged()
  }
  serviceWorkers?.addEventListener('controllerchange', changed)
  document.addEventListener('visibilitychange', visibility)
  window.addEventListener('online', online)
  window.addEventListener('pageshow', visibility)
  const stop = watch([
    () => appUpdates.state.ready,
    dependencies.active,
    dependencies.blocked,
    () => reloadSafety.blockers,
  ], () => appUpdates.reconsider(), { flush: 'sync' })

  activateSW = registerSW({
    immediate: true,
    onRegisteredSW(_url, value) {
      if (disposed || !value) return
      registration = value
      if (value.waiting) appUpdates.available()
      check()
      interval = setInterval(() => {
        if (document.visibilityState === 'visible') check()
      }, UPDATE_CHECK_INTERVAL_MS)
    },
    onNeedRefresh() {
      // The registration callback also handles a worker already waiting at startup.
      // Ignore late Workbox events for a worker another tab has already activated.
      if (!disposed && registration?.waiting) appUpdates.available()
    },
    onNeedReload: changed,
  })

  return () => {
    disposed = true
    stop()
    clearInterval(interval)
    serviceWorkers?.removeEventListener('controllerchange', changed)
    document.removeEventListener('visibilitychange', visibility)
    window.removeEventListener('online', online)
    window.removeEventListener('pageshow', visibility)
    appUpdates.dispose()
  }
}
